import { useRef, useState } from 'react';
import { Box } from '@mui/material';
import PropTypes from 'prop-types';
import StatValue from './StatValue';
import StatTitle from './StatTitle';
import { valueEms } from './statValueSize';

// App-local grid for "Single" MatchStats tiles. Mirrors the shared
// @ae/scoreboard CopilotStatsGrid (dark cards, team accent, grey number tiles,
// arrow-key nav) and adds an `activeKey` highlight — the tile whose caption is
// currently animated IN gets a persistent yellow ring + ON-AIR dot. Kept
// app-local so the highlight needs no shared-library rebuild.

const CARD_BG = '#2b2b2b';
const TEXT = '#ffffff';
const TEXT_SHADOW = '0 1px 4px rgba(0,0,0,0.7)';
const FALLBACK_HOME = '#2f6fd0';
const FALLBACK_AWAY = '#3aa564';
const LIVE = '#feec67';
const COLS = 5;
// Titles start at the copilot shortcut buttons' size (TXLive) and shrink only to fit.
const TITLE_PX = 22;
const TITLE_MIN_PX = 12;
const TITLE_LINE_HEIGHT = 1.15;
// One- and two-digit values share one size; only wider values go smaller.
const TWO_DIGIT_EMS = valueEms('00');
const fitEmsOf = (value) => Math.max(valueEms(value), TWO_DIGIT_EMS);

export default function CopilotSingleStatsGrid({ stats = [], leftColor, rightColor, activeKey, onStatClick, scale = 1 }) {
  const home = leftColor || FALLBACK_HOME;
  const away = rightColor || FALLBACK_AWAY;
  const s = (px) => Math.round(px * scale);
  const titlePx = s(TITLE_PX);
  const titleAreaPx = Math.ceil(2 * titlePx * TITLE_LINE_HEIGHT);
  const gridRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const tabIdx = Math.min(activeIdx, Math.max(0, stats.length - 1));

  const handleKeyDown = (e) => {
    if (!['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].includes(e.key)) return;
    const btns = Array.from(gridRef.current?.querySelectorAll('[data-stat-btn]') ?? []);
    if (!btns.length) return;
    const idx = btns.indexOf(document.activeElement);
    if (idx === -1) return;
    e.preventDefault();
    e.stopPropagation();
    const delta = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowDown' ? COLS : -COLS;
    const next = Math.max(0, Math.min(btns.length - 1, idx + delta));
    setActiveIdx(next);
    btns[next]?.focus();
  };

  return (
    <Box
      ref={gridRef}
      onKeyDown={handleKeyDown}
      sx={{
        // 8px past the panel on the sides and bottom: room for the tiles'
        // rings, while the tiles themselves line up with the column edges.
        height: 'calc(100% + 8px)',
        width: 'calc(100% + 16px)',
        overflowY: 'auto',
        display: 'grid',
        gridTemplateColumns: `repeat(${COLS}, 1fr)`,
        // Three rows share the height the panel gives, so they fit without scrolling.
        gridTemplateRows: 'repeat(3, minmax(0, 1fr))',
        gridAutoRows: `${s(150)}px`,
        gap: 1.5,
        px: 1,
        mx: -1,
        mb: -1,
        pt: 0.75,
        pb: 1,
        alignContent: 'start',
      }}
    >
      {stats.map((stat, i) => {
        const isLive = activeKey && stat.key === activeKey;
        return (
          <Box
            key={stat.key}
            component="button"
            data-stat-btn="true"
            tabIndex={i === tabIdx ? 0 : -1}
            onFocus={() => setActiveIdx(i)}
            onClick={() => onStatClick?.(stat)}
            className="no-drag"
            sx={{
              position: 'relative',
              display: 'flex',
              gap: 0,
              p: 0,
              bgcolor: 'transparent',
              border: 'none',
              borderRadius: 2,
              cursor: 'pointer',
              transition: 'box-shadow 0.15s ease, transform 0.1s ease',
              outline: 'none',
              boxShadow: isLive ? `0 0 0 3px ${LIVE}, 0 0 14px rgba(254,236,103,0.5)` : 'none',
              '&:hover': { boxShadow: isLive ? `0 0 0 3px ${LIVE}, 0 0 14px rgba(254,236,103,0.5)` : `0 0 0 2px ${LIVE}` },
              '&:focus-visible': { boxShadow: `0 0 0 2px ${LIVE}` },
              '&:active': { transform: 'scale(0.99)' },
            }}
          >
            <Box sx={{ flex: 1, bgcolor: CARD_BG, borderTop: `4px solid ${home}`, borderRadius: '8px 0 0 8px' }} />
            <Box sx={{ flex: 1, bgcolor: CARD_BG, borderTop: `4px solid ${away}`, borderRadius: '0 8px 8px 0' }} />

            {/* Live indicator — green dot for the on-air tile. */}
            {isLive && (
              <Box sx={{ position: 'absolute', top: 8, right: 8, width: 11, height: 11, borderRadius: '50%', bgcolor: '#22c55e', boxShadow: '0 0 8px #22c55e', zIndex: 2 }} />
            )}

            {/* Every tile has the same two-line title area, so the values line up
                across tiles and take all the height under it. */}
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', gap: 1, px: 0.5, pt: 1.5, pb: 1.5 }}>
              <Box sx={{ height: titleAreaPx, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                <StatTitle
                  maxPx={titlePx}
                  minPx={s(TITLE_MIN_PX)}
                  sx={{ width: '100%', textAlign: 'center', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', lineHeight: TITLE_LINE_HEIGHT, color: TEXT, textShadow: TEXT_SHADOW }}
                >
                  {stat.label}
                </StatTitle>
              </Box>
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
                {[stat.leftValue, stat.rightValue].map((val, vi) => (
                  <Box key={vi} sx={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center', overflow: 'hidden' }}>
                    <StatValue value={val} maxPx={s(72)} width="94%" fillHeight fitEms={fitEmsOf(val)} />
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

CopilotSingleStatsGrid.propTypes = {
  stats: PropTypes.array,
  leftColor: PropTypes.string,
  rightColor: PropTypes.string,
  activeKey: PropTypes.string,
  onStatClick: PropTypes.func,
  scale: PropTypes.number,
};
